import React from 'react';
import { StageSection, Prose, KeyPoints } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'The P2P Network & Node Validation',
    tagline: 'No server in the middle, just nodes checking each other\'s work.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - what a full node actually checks, and why hashpower doesn't override it
//   Q1 - the trade-off a lightweight (SPV) wallet makes vs. a full node
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 8 of 8" title="A network with no central server">
            <Prose>
                Bitcoin has no headquarters and no central server. It's a peer-to-peer network: each node connects
                to a handful of other nodes, relays transactions and blocks it hears about, and keeps its own
                independent copy of the entire blockchain.
            </Prose>
        </StageSection>
    )),

    content('full-node-validation', () => (
        <StageSection title="A full node trusts nobody: it checks everything itself">
            <Prose>
                Receiving a block doesn't mean accepting it. A full node independently re-verifies every rule: every
                signature is checked, every input must reference a real, unspent UTXO, the proof-of-work must
                actually meet the difficulty target, and the block reward and fees must add up correctly. If a
                miner (no matter how much hashpower they control) produces a block that breaks any of these
                rules, honest full nodes simply reject it. Hashpower can win the race to propose a block; it cannot
                force the network to accept an invalid one.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('spv-tradeoff', () => (
        <StageSection title="Full nodes vs. lightweight wallets">
            <Prose>
                Most mobile wallets don't download and check the entire blockchain themselves; that would be
                impractical on a phone. Instead, they act as lightweight (SPV) clients: they can use a Merkle proof
                (from Stage 6) to confirm a specific transaction is included in a block, but they rely on the full
                nodes they connect to for enforcing the network's rules, rather than checking every rule themselves.
            </Prose>
            <KeyPoints items={[
                <>Running your own full node is the only way to verify Bitcoin's rules for yourself, rather than trusting someone else's node to have done it.</>,
                <>Every idea in this track (keys, UTXOs, transactions, Script, blocks, mining) exists to support this one property: nobody has to take anyone else's word for what's true.</>,
            ]} />
        </StageSection>
    )),

    question(1),
];
